import { ICommandBarItemProps, IContextualMenuItem, ITheme, mergeStyleSets, useTheme } from '@fluentui/react';
import AppContext from 'App/AppContext';
import { IPCommandBar, IPText, IPTextField } from 'baseComponents';
import AuditCriterionPanel from 'components/AuditCriteria/AuditCriterionPanel';
import { IEvidenceTaskLinkItem } from 'components/AuditCriteria/EvidenceTaskLinks';
import { AIActionsButton, AIActionsButtonVariant } from 'components/Buttons/AIActionsButton';
import DialogConfirmGeneric from 'components/Dialogs/DialogConfirmGeneric';
import {
  criteriaAdviseEvidenceTemplate,
  criteriaGenerateTemplate,
  criteriaReevaluateTemplate,
} from 'components/Tasks/Forms/taskTypeFormAIActionTemplates';
import { AIActionBinding, AIActionGroup, createAIActionBinding } from 'models/copilot/actions/aiActionBinding';
import DialogYesNo from 'components/Dialogs/DialogYesNo';
import GenericModal from 'components/Dialogs/GenericModal';
import FieldPanel from 'components/KPI/FieldPanel';
import { hasKPIDefinitionChange } from 'components/KPI/KPIHelpers';
import KPIPreviewModal from 'components/KPI/KPIPreviewModal';
import OverlayLoader from 'components/Loading/OverlayLoader';
import { FormFieldModal } from 'components/Tasks/Forms/FormFieldModal';
import { FormStructureAddControl, IFormStructureAddOption } from 'components/Tasks/Forms/FormStructureAddControl';
import { filterFormReuseCandidates } from 'components/Tasks/Forms/taskTypeFormEditor.helpers';
import TaskTypeFormRenderer, { getVisibleFormElements } from 'components/Tasks/Forms/TaskTypeFormRenderer';
import AuditCriteriaSet, { auditCriterionVerificationManual, auditEntityKeyString } from 'models/auditCriteria';
import { AuditCriteriaLayer } from 'models/auditCriteriaGeneric';
import {
  ControlSceneAIActionsPreviewFeatureToggleCode,
  isPreviewFeatureToggleEnabled,
} from 'models/copilot/copilotPreviewFeatureToggles';
import { EntityTypes } from 'models/entity';
import KPI from 'models/kpi/kpi';
import { KPI_Translation } from 'models/kpi/kpi_Translation';
import KPIData from 'models/kpi/kpiData';
import { KPIDefinitionCommitResult, KPIDefinitionPreview } from 'models/kpi/kpiDefinitionRevision';
import ResourceLink from 'models/resourceLink';
import { AIEnabled } from 'models/setting';
import Task from 'models/tasks/task';
import TaskType from 'models/tasks/taskType';
import TaskTypeFormElement, { TaskTypeFieldType } from 'models/tasks/taskTypeFormElement';
import { TaskTypeFormElement_Translation } from 'models/tasks/taskTypeFormElement_Translation';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  apiAdviseAuditCriteriaEvidenceTasks,
  apiGenerateAuditCriteria,
  apiGetAuditCriteria,
  apiQueryAuditCriteriaStatus,
  apiReevaluateAuditCriteria,
  apiSaveAuditCriteria,
} from 'services/Api/auditCriteriaService';
import {
  apiAddKPI,
  apiCommitKPIDefinition,
  apiCreateKPIWithFormPlacement,
  apiPreviewKPIDefinition,
  apiUpdateKPIStableMetadata,
} from 'services/Api/kpiService';
import { apiRequest } from 'services/Auth/authConfig';
import { FeatureTypes, hasUserFeature, hasUserFeatureGenericManager } from 'services/Auth/featurePermissions';
import { moveItem } from 'utils/array';
import { getLocalStorageData, LocalStorageKeys, setLocalStorageData } from 'utils/localstorage';
import { toBool } from 'utils/string';

export interface ITaskTypeFormEditorProps {
  taskTypeId: number;
  formTaskType?: TaskType;
  getCurrentFormTaskType?: () => TaskType | undefined;
  kpis: KPI[];
  formElements: TaskTypeFormElement[];
  editable: boolean;
  readonly: boolean;
  isSystemForm: boolean;
  isLoading: boolean;
  compact?: boolean;
  hideEmptyFormText?: boolean;
  designTime?: boolean;
  fieldPanelAsPanel?: boolean;
  formFieldModalAsPanel?: boolean;
  showFieldCards?: boolean;
  persistPreviewDismissed?: boolean;
  // Ask for confirmation before removing a field. Set by autosaving hosts (Formulieren library),
  // where a delete persists immediately; the task-form modal keeps Annuleren as its undo.
  confirmFieldRemoval?: boolean;
  // Commands the HOST owns that belong on the form's own toolbar, next to Add. The host owns them
  // because they act on its autosave edit session (its Discard), not on anything the editor knows
  // about. Only offered on an editable form: that is when the toolbar exists at all.
  toolbarCommands?: IFormStructureAddOption[];
  criteriaCommandBarItems?: ICommandBarItemProps[];
  onElementsChange: (next: TaskTypeFormElement[], opts: { persist: boolean }) => void;
  onStructureCommitted?: (taskType: TaskType, formElements: TaskTypeFormElement[]) => void;
  onSavingChange?: (saving: boolean) => void;
  onBeforeDefinitionCommit?: () => Promise<boolean>;
  onDefinitionCommitted?: (result: KPIDefinitionCommitResult) => void;
  // Notify the parent when the KPI pool changes (a field was created/edited here) so its reuse
  // candidates and formula-operand list stay fresh without a full reload.
  onKPIsChange?: (next: KPI[]) => void;
  // Entity names determine criteria grouping and command targets.
  entityNamesByKey?: Map<string, string>;
  // Criteria writes re-project the form. Hosts must reload the elements after this callback.
  onCriteriaChanged?: () => void;
  // The evidence tasks bound to a one-time check, keyed by lower-cased criterionKey. The criteria
  // editor passes them for the colour of the one-time badge and to know whether the AI advice command
  // has anything to work on; the cards do not link them here, the criterion editor lists them.
  evidenceTasksByCriterionKey?: Map<string, IEvidenceTaskLinkItem[]>;
  // The criteria editor's further card pills ("Answered by", the latest AI outcome), by lower-cased criterionKey.
  criterionBadgesByKey?: Map<string, string[]>;
  // The criteria editor's "show archived" toggle: render retired criterion cards nobody answered.
  showRetired?: boolean;
  // Let the form flow to its natural height and leave the scrolling to the host. For a host that is
  // not a fixed-height box - a panel - see TaskTypeFormRenderer.disableScrollContainer.
  disableScrollContainer?: boolean;
  // There is no form behind these elements - they are a local projection of one entity's criteria
  // (see mappings/auditCriteriaFormProjection), so the only things one can do here are the criteria
  // commands. Adding, editing, removing and moving a FIELD is off: nothing would persist it.
  //
  // It also carries its own permission gate: this mode is the criteria surface itself, so the host's
  // `editable` decides, where a procedure form additionally asks whether criteria editing is offered
  // on that route at all.
  criteriaOnly?: boolean;
}

const getStyles = (theme: ITheme) =>
  mergeStyleSets({
    fieldCard: {
      borderRadius: 3,
      padding: '2px 8px',
      backgroundColor: theme.semanticColors.bodyBackground,
    },
  });

// A KPI read back from a save carries no answer row: those endpoints return the definition alone. An
// element whose KPI has no row renders nothing, so the field would drop off the form until the next
// read - give it the row the element already had, the preview answer included.
const keepFormAnswer = (kpi: KPI, previous: KPI | undefined) => {
  if (kpi.data.length > 0) return;
  kpi.data = previous && previous.data.length > 0 ? previous.data.map((d) => d.clone()) : [new KPIData()];
};

const TaskTypeFormEditor = (props: ITaskTypeFormEditorProps) => {
  const { t } = useTranslation(['fieldsLibrary', 'forms', 'kpis', 'translation', 'criteria']);
  const appContext = useContext(AppContext);
  const theme = useTheme();
  const styles = getStyles(theme);

  const [task, setTask] = useState<Task>(() => {
    const dummy = new Task();
    dummy.taskTypeId = props.taskTypeId;

    return dummy;
  });
  const [taskLinks, setTaskLinks] = useState<ResourceLink[]>([]);
  const [showAddPanel, setShowAddPanel] = useState<boolean>(false);
  // "Create new" in the add panel's reuse picker hands off here: re-seed (fresh name + tracked KPI)
  // and bump the key so the add panel remounts and animates in as a new panel.
  const [addPanelKey, setAddPanelKey] = useState<number>(0);
  const [addPanelSeed, setAddPanelSeed] = useState<{ name?: string; isField: boolean; fromCreateNew?: boolean }>({
    isField: true,
  });
  const [showEditPanel, setShowEditPanel] = useState<boolean>(false);
  const [kpiToEdit, setKPIToEdit] = useState<KPI | undefined>(undefined);
  const [kpiToPreview, setKPIToPreview] = useState<KPI | undefined>(undefined);
  const [previewDismissed, setPreviewDismissed] = useState<boolean>(() =>
    props.persistPreviewDismissed
      ? toBool(getLocalStorageData(appContext, LocalStorageKeys.FieldsPreviewHidden))
      : false,
  );
  const [elementToRemove, setElementToRemove] = useState<TaskTypeFormElement | undefined>(undefined);
  const [showFieldEditor, setShowFieldEditor] = useState<boolean>(false);
  const [fieldFormAddMode, setFieldFormAddMode] = useState<boolean>(false);
  const [formElementToEdit, setFormElementToEdit] = useState<TaskTypeFormElement | undefined>(undefined);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  // The criterion editor: which entity's set is open and which criterion in it (undefined adds one).
  const [criterionTarget, setCriterionTarget] = useState<
    { entityType: number; entityId: number; criterionKey?: string } | undefined
  >(undefined);
  const [criterionSet, setCriterionSet] = useState<AuditCriteriaSet | undefined>(undefined);
  const [generateTarget, setGenerateTarget] = useState<{ entityType: number; entityId: number } | undefined>(undefined);
  const [criterionToRemove, setCriterionToRemove] = useState<TaskTypeFormElement | undefined>(undefined);
  // The covered controls that have child items (auditEntityKeyString keys): they carry no criteria.
  const [parentEntityKeys, setParentEntityKeys] = useState<Set<string>>(new Set());
  // where each audited item's criteria come from (PLAN-075), by item key: the per-item block names it
  const [entityLayers, setEntityLayers] = useState<Map<string, AuditCriteriaLayer>>(new Map());
  const [isCriteriaWorking, setIsCriteriaWorking] = useState<boolean>(false);
  const [criteriaWorkingText, setCriteriaWorkingText] = useState<string | undefined>(undefined);
  const [pendingKPIDefinition, setPendingKPIDefinition] = useState<
    { kpi: KPI; preview: KPIDefinitionPreview } | undefined
  >(undefined);
  const [kpiDefinitionReason, setKPIDefinitionReason] = useState<string>('');

  useEffect(() => {
    if (!isCriteriaWorking && !props.isLoading) setCriteriaWorkingText(undefined);
  }, [isCriteriaWorking, props.isLoading]);

  // The criteria of an entity are shared by every procedure that covers it, so editing them here
  // carries the same gate as the other criteria surfaces. A host that cannot reload the projected
  // form afterwards does not pass onCriteriaChanged and keeps the cards locked. An ARCHIVED form is
  // history whatever the gate says: nothing on it can be changed from here, the AI section commands
  // included.
  const canManageCriteria =
    !!props.onCriteriaChanged &&
    !props.formTaskType?.isArchived &&
    (props.criteriaOnly
      ? props.editable
      : isPreviewFeatureToggleEnabled(
          appContext.globalDataCache.settings,
          ControlSceneAIActionsPreviewFeatureToggleCode,
        ) && hasUserFeatureGenericManager(appContext));

  const criteriaAiEnabled =
    hasUserFeature(appContext, FeatureTypes.AIAssistant) &&
    (appContext.globalDataCache.settings.get(AIEnabled) as boolean) === true;

  useEffect(() => {
    const dummy = new Task();
    dummy.taskTypeId = props.taskTypeId;
    setTask(dummy);
    setTaskLinks([]);
  }, [props.taskTypeId]);

  // Must keep its identity across renders: the field panels debounce on it, and a new function on every
  // render restarts that debounce, which replaces the preview again, which renders again - an endless
  // loop that wipes any answer given in the preview within 200 ms.
  const updatePreviewKPI = useCallback((kpi: KPI) => setKPIToPreview(kpi), []);

  const togglePreview = () =>
    setPreviewDismissed((d) => {
      const next = !d;
      if (props.persistPreviewDismissed) {
        setLocalStorageData(appContext, LocalStorageKeys.FieldsPreviewHidden, next.toString());
      }

      return next;
    });

  const notifyChange = (next: TaskTypeFormElement[], persist: boolean) => {
    props.onElementsChange(next, { persist });
  };

  const updateKPILocal = (kpi: KPI) => {
    const idx = props.formElements.findIndex((e) => e.kpiId === kpi.kpiId);
    if (idx < 0) return;
    const next = [...props.formElements];
    next[idx] = next[idx].clone();
    keepFormAnswer(kpi, next[idx].kpi);
    next[idx].kpi = kpi;
    notifyChange(next, false);
  };

  const onUpdateValue = (kpi: KPI, data: KPIData) => {
    const newKPI = kpi.clone();
    newKPI.data = [data];
    updateKPILocal(newKPI);
  };

  const onUpdateField = (elm: TaskTypeFormElement): TaskTypeFormElement[] => {
    const next = props.formElements.map((e) => (e.key === elm.key ? elm : e));
    notifyChange(next, true);

    return next;
  };

  const removeElement = (elm: TaskTypeFormElement) => {
    notifyChange(
      props.formElements.filter((e) => e.key !== elm.key),
      true,
    );
  };

  const onRemove = (elm: TaskTypeFormElement) => {
    if (props.confirmFieldRemoval) {
      setElementToRemove(elm);

      return;
    }
    removeElement(elm);
  };

  // A generated criterion never moves, and nothing moves past one: the projector re-numbers the block
  // it writes on every sync, so a question dropped inside it would be shuffled out again anyway. Only
  // an own question has move buttons at all; this is what stops it from being pushed into the block.
  const blockedByCriterionField = (index: number): boolean => {
    const neighbour = props.formElements[index];

    return !!neighbour?.criterionKey;
  };

  const onMoveUp = (elm: TaskTypeFormElement) => {
    const fromIdx = props.formElements.findIndex((e) => e.key === elm.key);
    if (fromIdx <= 0 || blockedByCriterionField(fromIdx - 1)) return;
    const next = [...props.formElements];
    moveItem(next, fromIdx, fromIdx - 1);
    next.forEach((e, idx) => (e.sortOrder = idx));
    notifyChange(next, true);
  };

  const onMoveDown = (elm: TaskTypeFormElement) => {
    const fromIdx = props.formElements.findIndex((e) => e.key === elm.key);
    if (fromIdx === props.formElements.length - 1 || blockedByCriterionField(fromIdx + 1)) return;
    const next = [...props.formElements];
    moveItem(next, fromIdx, fromIdx + 1);
    next.forEach((e, idx) => (e.sortOrder = idx));
    notifyChange(next, true);
  };

  //
  // Criteria: the pencil on a criterion card and the Add/Generate/Sync commands on the entity
  // headings edit the entity's shared criteria set. Every write is immediate (the server forks/
  // retires/re-projects), after which the host reloads the projected form via onCriteriaChanged.
  //
  const openCriterionEditor = async (entityType: number, entityId: number, criterionKey?: string) => {
    try {
      setIsCriteriaWorking(true);
      const accessToken = await appContext.getAccessToken(apiRequest.scopes);
      const set = await apiGetAuditCriteria(entityType, entityId, accessToken);
      // A parent carries no criteria and the server refuses every write to its set.
      if (!set.isLeaf) {
        appContext.showNotification(t('criteria:Editor.ParentNode'), false, true, true);

        return;
      }
      setCriterionSet(set);
      setCriterionTarget({ entityType, entityId, criterionKey });
    } catch (err) {
      appContext.setError(err);
    } finally {
      setIsCriteriaWorking(false);
    }
  };

  const onEditCriterion = (field: TaskTypeFormElement) => {
    if (field.entityType === undefined || field.entityId === undefined || !field.criterionKey) return;
    void openCriterionEditor(field.entityType, field.entityId, field.criterionKey);
  };

  // Deleting a criterion is a criteria-set write, exactly like the criterion panel's Delete: the
  // audited item owns the question, so taking the field off this form would only bring it back on
  // the next projection. Posting an empty list clears the set, which is what the server expects.
  const deleteCriterion = async (field: TaskTypeFormElement) => {
    if (field.entityType === undefined || field.entityId === undefined || !field.criterionKey) return;

    try {
      setIsCriteriaWorking(true);
      const accessToken = await appContext.getAccessToken(apiRequest.scopes);
      const set = await apiGetAuditCriteria(field.entityType, field.entityId, accessToken);
      const next = set.criteria
        .filter((criterion) => criterion.criterionKey.toLowerCase() !== field.criterionKey?.toLowerCase())
        .map((criterion) => criterion.clone());
      if (next.length === set.criteria.length) return;
      await apiSaveAuditCriteria(field.entityType, field.entityId, next, accessToken);
      props.onCriteriaChanged?.();
    } catch (err) {
      appContext.setError(err);
    } finally {
      setIsCriteriaWorking(false);
      setCriterionToRemove(undefined);
    }
  };

  // The move buttons on a criterion card reorder the entity's criteria ARRAY, like the drag path -
  // the projection follows that order. Moving is by neighbour IN THE SET, so an archived criterion
  // in between keeps its place instead of being jumped over silently.
  const moveCriterion = async (field: TaskTypeFormElement, offset: number) => {
    if (field.entityType === undefined || field.entityId === undefined || !field.criterionKey) return;

    try {
      setIsCriteriaWorking(true);
      const accessToken = await appContext.getAccessToken(apiRequest.scopes);
      const set = await apiGetAuditCriteria(field.entityType, field.entityId, accessToken);
      const fromIdx = set.criteria.findIndex(
        (criterion) => criterion.criterionKey.toLowerCase() === field.criterionKey?.toLowerCase(),
      );
      const toIdx = fromIdx + offset;
      if (fromIdx < 0 || toIdx < 0 || toIdx >= set.criteria.length) return;
      const next = set.criteria.map((criterion) => criterion.clone());
      moveItem(next, fromIdx, toIdx);
      await apiSaveAuditCriteria(field.entityType, field.entityId, next, accessToken);
      props.onCriteriaChanged?.();
    } catch (err) {
      appContext.setError(err);
    } finally {
      setIsCriteriaWorking(false);
    }
  };

  const generateCriteria = async () => {
    const target = generateTarget;
    if (!target) return;
    setGenerateTarget(undefined);

    try {
      setCriteriaWorkingText(t('criteria:Editor.Progress.Regenerating'));
      setIsCriteriaWorking(true);
      const accessToken = await appContext.getAccessToken(apiRequest.scopes);
      await apiGenerateAuditCriteria(target.entityType, target.entityId, accessToken);
      appContext.showNotification(t('criteria:Editor.Generated'), false, false, true);
      props.onCriteriaChanged?.();
    } catch (err) {
      appContext.setError(err);
    } finally {
      setIsCriteriaWorking(false);
    }
  };

  const reevaluateCriteria = async (entityType: number, entityId: number) => {
    try {
      setCriteriaWorkingText(t('criteria:Editor.Progress.Syncing'));
      setIsCriteriaWorking(true);
      const accessToken = await appContext.getAccessToken(apiRequest.scopes);
      await apiReevaluateAuditCriteria(entityType, entityId, accessToken);
      appContext.showNotification(t('criteria:Editor.Synced'), false, false, true);
      props.onCriteriaChanged?.();
    } catch (err) {
      appContext.setError(err);
    } finally {
      setIsCriteriaWorking(false);
    }
  };

  // Advice only: the AI judges the evidence tasks bound to the entity's one-time criteria and stores
  // its verdict per task. Completing or archiving a task is what verifies a criterion.
  const adviseEvidenceTasks = async (entityType: number, entityId: number) => {
    try {
      setCriteriaWorkingText(t('criteria:Editor.Progress.Advising'));
      setIsCriteriaWorking(true);
      const accessToken = await appContext.getAccessToken(apiRequest.scopes);
      await apiAdviseAuditCriteriaEvidenceTasks(entityType, entityId, accessToken);
      appContext.showNotification(t('criteria:Editor.Advised'), false, false, true);
      props.onCriteriaChanged?.();
    } catch (err) {
      appContext.setError(err);
    } finally {
      setIsCriteriaWorking(false);
    }
  };

  // A dragged criterion card reorders the entity's criteria ARRAY (= the projection order): load
  // the set, move the criterion, PUT. The signature is order-insensitive server-side, so a pure
  // reorder does not invalidate evidence caches.
  const reorderCriteria = async (from: TaskTypeFormElement, to: TaskTypeFormElement) => {
    if (from.entityType === undefined || from.entityId === undefined || !from.criterionKey || !to.criterionKey) return;

    try {
      setIsCriteriaWorking(true);
      const accessToken = await appContext.getAccessToken(apiRequest.scopes);
      const set = await apiGetAuditCriteria(from.entityType, from.entityId, accessToken);
      const fromIdx = set.criteria.findIndex(
        (criterion) => criterion.criterionKey.toLowerCase() === from.criterionKey?.toLowerCase(),
      );
      const toIdx = set.criteria.findIndex(
        (criterion) => criterion.criterionKey.toLowerCase() === to.criterionKey?.toLowerCase(),
      );
      if (fromIdx < 0 || toIdx < 0 || fromIdx === toIdx) return;
      const next = set.criteria.map((criterion) => criterion.clone());
      const [moved] = next.splice(fromIdx, 1);
      next.splice(toIdx, 0, moved);
      await apiSaveAuditCriteria(from.entityType, from.entityId, next, accessToken);
      props.onCriteriaChanged?.();
    } catch (err) {
      appContext.setError(err);
    } finally {
      setIsCriteriaWorking(false);
    }
  };

  // Drag & drop follows the move buttons' rules: an own question never crosses into the generated
  // block, a criterion card only moves within its own entity's section.
  const onDragMove = (from: TaskTypeFormElement, to: TaskTypeFormElement) => {
    if (from.criterionKey) {
      if (!canManageCriteria || isCriteriaWorking) return;
      if (!to.criterionKey || to.entityType !== from.entityType || to.entityId !== from.entityId) return;
      void reorderCriteria(from, to);

      return;
    }

    if (to.criterionKey) return;
    const fromIdx = props.formElements.findIndex((e) => e.key === from.key);
    const toIdx = props.formElements.findIndex((e) => e.key === to.key);
    if (fromIdx < 0 || toIdx < 0 || fromIdx === toIdx) return;
    const [min, max] = fromIdx < toIdx ? [fromIdx + 1, toIdx] : [toIdx, fromIdx - 1];
    for (let i = min; i <= max; i++) {
      if (props.formElements[i]?.criterionKey) return;
    }
    const next = [...props.formElements];
    moveItem(next, fromIdx, toIdx);
    next.forEach((e, idx) => (e.sortOrder = idx));
    notifyChange(next, true);
  };

  const attachKPIs = (items: KPI[]) => {
    const next = [...props.formElements];
    items.forEach((item) => {
      if (!item.data || item.data.length === 0) {
        const data = new KPIData();
        data.kpi = item;
        item.data = [data];
      }
      const newElm = new TaskTypeFormElement();
      newElm.taskTypeId = props.taskTypeId;
      newElm.fieldType = TaskTypeFieldType.KPI;
      newElm.kpi = item;
      newElm.kpiId = item.kpiId;
      newElm.kpiDefinitionRevisionId = item.currentDefinitionRevisionId;
      newElm.sortOrder = next.length;
      next.push(newElm);
    });
    notifyChange(next, true);
  };

  const addField = (elm: TaskTypeFormElement): TaskTypeFormElement[] => {
    const next = [...props.formElements, elm];
    notifyChange(next, true);

    return next;
  };

  // The add panel, the KPI edit panel and the field editor share the right side-bar layer host and
  // leave the form clickable, so at most one of them is open: each open path closes the others first.
  const closeSidePanels = () => {
    setShowAddPanel(false);
    setShowEditPanel(false);
    setShowFieldEditor(false);
  };

  // The field editor has no live preview; a preview left by a closed KPI panel would re-anchor to the
  // form itself.
  const openFieldEditor = (elm: TaskTypeFormElement, addMode: boolean) => {
    closeSidePanels();
    setKPIToPreview(undefined);
    setFieldFormAddMode(addMode);
    setFormElementToEdit(elm);
    setShowFieldEditor(true);
  };

  const onAddNewField = () => {
    closeSidePanels();
    setAddPanelSeed({ isField: true });
    setShowAddPanel(true);
  };

  const onEdit = (elm: TaskTypeFormElement) => {
    if (elm.kpi) {
      closeSidePanels();
      setKPIToEdit(elm.kpi);
      setShowEditPanel(true);
    } else {
      openFieldEditor(elm, false);
    }
  };

  const onAddFormElement = (fieldType: TaskTypeFieldType) => {
    const newElm = new TaskTypeFormElement();
    newElm.taskTypeId = props.taskTypeId;
    newElm.fieldMode = 1;
    newElm.fieldType = fieldType;
    newElm.sortOrder = props.formElements.length;
    newElm.trans = [new TaskTypeFormElement_Translation()];
    newElm.transIdx = 0;
    openFieldEditor(newElm, true);
  };

  const onAddAttachment = () => onAddFormElement(TaskTypeFieldType.AttachmentDocument);

  const onAddContext = () => onAddFormElement(TaskTypeFieldType.ContextProcess);

  const setSaving = (saving: boolean) => {
    setIsSaving(saving);
    props.onSavingChange?.(saving);
  };

  const addKPI = async (kpi: KPI) => {
    try {
      setSaving(true);
      appContext.showContentLoader();
      kpi.trans = [new KPI_Translation()];
      kpi.transIdx = 0;
      const accessToken = await appContext.getAccessToken(apiRequest.scopes);
      const newElm = new TaskTypeFormElement();
      newElm.taskTypeId = props.taskTypeId;
      newElm.fieldType = TaskTypeFieldType.KPI;
      newElm.sortOrder = props.formElements.length;
      const nextElements = [...props.formElements, newElm];
      const flushed = await props.onBeforeDefinitionCommit?.();
      if (flushed === false) {
        return false;
      }

      const currentFormTaskType = props.getCurrentFormTaskType?.() ?? props.formTaskType;
      if (currentFormTaskType?.taskTypeFamilyId) {
        const result = await apiCreateKPIWithFormPlacement(
          kpi,
          currentFormTaskType,
          nextElements,
          nextElements.length - 1,
          accessToken,
          appContext.globalDataCache,
        );
        const newKPI = result.kpi;
        newKPI.data = [new KPIData()];
        const acceptedElements = (result.form.taskType.form ?? nextElements).map((element) => {
          if (element.kpiId !== newKPI.kpiId) {
            return element;
          }

          const next = element.clone();
          next.kpi = newKPI;
          next.kpiDefinitionRevisionId = newKPI.currentDefinitionRevisionId;

          return next;
        });
        props.onKPIsChange?.([...props.kpis, newKPI]);
        if (props.onStructureCommitted) {
          props.onStructureCommitted(result.form.taskType, acceptedElements);
        } else {
          notifyChange(acceptedElements, false);
        }

        return true;
      }

      const newKPI = await apiAddKPI(kpi, accessToken, appContext.globalDataCache);
      newKPI.data = [new KPIData()];
      newElm.kpi = newKPI;
      newElm.kpiId = newKPI.kpiId;
      newElm.kpiDefinitionRevisionId = newKPI.currentDefinitionRevisionId;
      notifyChange(nextElements, true);
      props.onKPIsChange?.([...props.kpis, newKPI]);

      return true;
    } catch (err) {
      appContext.setError(err);

      return false;
    } finally {
      setSaving(false);
      appContext.hideContentLoader();
    }
  };

  const hasDefinitionChange = (kpi: KPI): boolean =>
    hasKPIDefinitionChange(props.kpis.find((item) => item.kpiId === kpi.kpiId) ?? kpiToEdit, kpi);

  const applyDefinitionCommit = (result: KPIDefinitionCommitResult) => {
    const nextElements = props.formElements.map((element) => {
      if (element.kpiId !== result.kpi.kpiId) {
        return element;
      }

      const next = element.clone();
      keepFormAnswer(result.kpi, next.kpi);
      next.kpi = result.kpi;
      next.kpiDefinitionRevisionId = result.kpiDefinitionRevisionId;

      return next;
    });
    notifyChange(nextElements, false);
    props.onKPIsChange?.(props.kpis.map((item) => (item.kpiId === result.kpi.kpiId ? result.kpi : item)));
    props.onDefinitionCommitted?.(result);
  };

  const commitKPIDefinition = async (
    kpi: KPI,
    preview: KPIDefinitionPreview,
    reason: string | undefined,
  ): Promise<boolean> => {
    try {
      setSaving(true);
      appContext.showContentLoader();
      const accessToken = await appContext.getAccessToken(apiRequest.scopes);
      const result = await apiCommitKPIDefinition(kpi, preview.previewToken, reason, true, accessToken);
      applyDefinitionCommit(result);
      setPendingKPIDefinition(undefined);

      return true;
    } catch (err) {
      appContext.setError(err);

      return false;
    } finally {
      setSaving(false);
      appContext.hideContentLoader();
    }
  };

  const editKPI = async (kpi: KPI): Promise<boolean> => {
    try {
      setSaving(true);
      appContext.showContentLoader();
      const accessToken = await appContext.getAccessToken(apiRequest.scopes);
      if (!hasDefinitionChange(kpi)) {
        const updated = await apiUpdateKPIStableMetadata(kpi, accessToken);
        updateKPILocal(updated);
        props.onKPIsChange?.(props.kpis.map((item) => (item.kpiId === updated.kpiId ? updated : item)));

        return true;
      }

      const flushed = await props.onBeforeDefinitionCommit?.();
      if (flushed === false) {
        return false;
      }

      const preview = await apiPreviewKPIDefinition(kpi, accessToken);
      if (preview.compatibilityBlockers.length > 0) {
        appContext.showNotification(preview.compatibilityBlockers.join(' '), true, false, true);

        return false;
      }

      if (preview.requiresConfirmation) {
        setKPIDefinitionReason('');
        setPendingKPIDefinition({ kpi, preview });

        return false;
      }

      return await commitKPIDefinition(kpi, preview, undefined);
    } catch (err) {
      appContext.setError(err);

      return false;
    } finally {
      setSaving(false);
      appContext.hideContentLoader();
    }
  };

  // Reuse candidates = any non-system KPI OR field not already on this form, so typing a name that matches
  // an existing field offers to reuse it (field reuse across forms; the "used by multiple forms" warning
  // then flags the shared field). System KPIs are reporting-only and cannot store form data.
  const reuseCandidates = filterFormReuseCandidates(props.kpis, props.formElements);

  // KpiIds of the elements on the current form: formula operands are limited to fields on this
  // form (F10). Absent without form context.
  const formKpiIds = useMemo(() => props.formElements.flatMap((e) => (e.kpiId ? [e.kpiId] : [])), [props.formElements]);

  // The items this form asks criteria about. Taken from the names the host resolved, not from the
  // fields on the form: an item that has no criterion yet is exactly the one you want to add the
  // first criterion for, and it has no field to read it off.
  const criteriaEntities = useMemo(() => {
    const items: { entityType: number; entityId: number; name: string }[] = [];

    props.entityNamesByKey?.forEach((name, key) => {
      const [entityType, entityId] = key.split('|').map((part) => Number(part));
      if (Number.isNaN(entityType) || Number.isNaN(entityId)) return;
      items.push({ entityType, entityId, name });
    });

    return items;
  }, [props.entityNamesByKey]);

  // Criteria live on the leaf items only, so a covered control with child items is not offered for
  // adding one. Asked once for the covered items, which also says where each item's criteria come from
  // (PLAN-075); a missing answer counts as a leaf. The criteria editor host checks its one item itself.
  const criteriaEntityKeys = criteriaEntities
    .map((entity) => auditEntityKeyString(entity.entityType, entity.entityId))
    .join(',');

  useEffect(() => {
    if (!canManageCriteria || props.criteriaOnly || criteriaEntities.length === 0) {
      setParentEntityKeys(new Set());
      setEntityLayers(new Map());

      return;
    }

    let cancelled = false;
    const loadStatuses = async () => {
      try {
        const accessToken = await appContext.getAccessToken(apiRequest.scopes);
        const statuses = await apiQueryAuditCriteriaStatus(
          criteriaEntities.map((entity) => ({ entityType: entity.entityType, entityId: entity.entityId })),
          accessToken,
        );
        if (cancelled) return;
        setParentEntityKeys(
          new Set(
            statuses
              .filter((status) => status.entityType === (EntityTypes.Control as number) && !status.isLeaf)
              .map((status) => auditEntityKeyString(status.entityType, status.entityId)),
          ),
        );
        setEntityLayers(
          new Map(statuses.map((status) => [auditEntityKeyString(status.entityType, status.entityId), status.layer])),
        );
      } catch (err) {
        if (!cancelled) appContext.setError(err);
      }
    };

    void loadStatuses();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [criteriaEntityKeys, canManageCriteria, props.criteriaOnly]);

  // The per-item block's heading names where the item's criteria come from: a generic set or the item's own copy.
  // Design time only; the execution form shows no layers.
  const entityNamesWithLayer = useMemo(() => {
    if (!props.entityNamesByKey || entityLayers.size === 0) return props.entityNamesByKey;

    const names = new Map<string, string>();
    props.entityNamesByKey.forEach((name, key) => {
      const layer = entityLayers.get(key);
      if (layer === AuditCriteriaLayer.Family || layer === AuditCriteriaLayer.Subtype) {
        names.set(key, t('criteria:Generic.Layer.ItemGeneric', { name: name }));
      } else if (layer === AuditCriteriaLayer.Specific) {
        names.set(key, t('criteria:Generic.Layer.ItemOwn', { name: name }));
      } else {
        names.set(key, name);
      }
    });

    return names;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.entityNamesByKey, entityLayers]);

  const addableCriteriaEntities = criteriaEntities.filter(
    (entity) => !parentEntityKeys.has(auditEntityKeyString(entity.entityType, entity.entityId)),
  );

  // Whether the AI advice command has anything to work on: an active one-time criterion the AI may
  // judge, with at least one evidence task. Read off the local projection, which knows both.
  const hasAdvisableEvidence = useMemo(
    () =>
      !!props.criteriaOnly &&
      props.formElements.some(
        (element) =>
          !!element.criterionKey &&
          !element.retiredOn &&
          element.criterionOneTime === true &&
          element.criterionVerification !== auditCriterionVerificationManual &&
          (props.evidenceTasksByCriterionKey?.get(element.criterionKey.toLowerCase())?.length ?? 0) > 0,
      ),
    [props.criteriaOnly, props.formElements, props.evidenceTasksByCriterionKey],
  );

  // Adding a criterion by hand belongs with the other things one can add to a form, not next to the
  // AI commands: it is the same kind of act as adding a field. One audited item is the normal case
  // and adds straight away; several offer a choice (as separate cards on an empty form, which has no
  // menu to nest them under).
  const criteriaAddOptions = (asCards: boolean): IFormStructureAddOption[] => {
    if (!canManageCriteria || addableCriteriaEntities.length === 0) return [];

    if (addableCriteriaEntities.length === 1) {
      const entity = addableCriteriaEntities[0];

      return [
        {
          key: 'criterion',
          icon: 'ComplianceAudit',
          label: t('criteria:Editor.AddToForm'),
          onClick: () => void openCriterionEditor(entity.entityType, entity.entityId, undefined),
          disabled: isCriteriaWorking,
        },
      ];
    }

    if (asCards) {
      return addableCriteriaEntities.map((entity) => ({
        key: `criterion-${entity.entityType}-${entity.entityId}`,
        icon: 'ComplianceAudit',
        label: t('criteria:Editor.AddToFormForEntity', { name: entity.name }),
        onClick: () => void openCriterionEditor(entity.entityType, entity.entityId, undefined),
        disabled: isCriteriaWorking,
      }));
    }

    return [
      {
        key: 'criterion',
        icon: 'ComplianceAudit',
        label: t('criteria:Editor.AddToForm'),
        onClick: () => undefined,
        disabled: isCriteriaWorking,
        subMenuItems: addableCriteriaEntities.map((entity) => ({
          key: `criterion-${entity.entityType}-${entity.entityId}`,
          text: entity.name,
          onClick: () => void openCriterionEditor(entity.entityType, entity.entityId, undefined),
        })),
      },
    ];
  };

  // The criteria actions of every entity the form serves. No assistant binding: this menu is the
  // form's own work, not a chat entry point.
  const criteriaActionBindings: AIActionBinding[] = canManageCriteria
    ? criteriaEntities.flatMap((entity) => {
        const bindings: AIActionBinding[] = [];
        const params = {
          entityType: entity.entityType,
          entityId: entity.entityId,
          entityName: entity.name,
          withEntityName: criteriaEntities.length > 1,
        };
        const options = { group: AIActionGroup.Other, disabled: isCriteriaWorking || isSaving };

        // Criteria AI commands operate on the full set and are available only in the source criteria editor.
        // Procedure form editors keep the manual field controls without exposing these set-level commands.
        if (criteriaAiEnabled && props.criteriaOnly) {
          bindings.push(
            createAIActionBinding(
              criteriaReevaluateTemplate,
              { ...params, run: () => void reevaluateCriteria(entity.entityType, entity.entityId) },
              { ...options, title: t('criteria:Editor.ReevaluateInfo') },
            ),
            createAIActionBinding(
              criteriaGenerateTemplate,
              {
                ...params,
                run: () => setGenerateTarget({ entityType: entity.entityType, entityId: entity.entityId }),
              },
              { ...options, title: t('criteria:Editor.UpdateGenerateInfo') },
            ),
          );

          // The advice runs through the control audit's evidence inspection, so controls only.
          if (hasAdvisableEvidence && entity.entityType === (EntityTypes.Control as number)) {
            bindings.push(
              createAIActionBinding(
                criteriaAdviseEvidenceTemplate,
                { ...params, run: () => void adviseEvidenceTasks(entity.entityType, entity.entityId) },
                { ...options, title: t('criteria:Editor.AdviseEvidenceInfo') },
              ),
            );
          }
        }

        return bindings;
      })
    : [];

  const addOptions: IFormStructureAddOption[] = [
    {
      key: 'field',
      icon: 'Add',
      label: t('fieldsLibrary:Detail.NewField'),
      onClick: onAddNewField,
      title: t('forms:Dialogs.EditForm.AddNew.NewKPIInfo'),
    },
    {
      key: 'attachment',
      icon: 'Attach',
      label: t('forms:Dialogs.EditForm.AddNew.NewAttachment'),
      onClick: onAddAttachment,
      disabled: props.isSystemForm,
      title: props.isSystemForm
        ? t('forms:Dialogs.EditForm.AddNew.NewAttachmentInfoDisabled')
        : t('forms:Dialogs.EditForm.AddNew.NewAttachmentInfo'),
    },
    {
      key: 'context',
      icon: 'Link',
      label: t('forms:Dialogs.EditForm.AddNew.NewContext'),
      onClick: onAddContext,
      disabled: props.isSystemForm,
      title: props.isSystemForm
        ? t('forms:Dialogs.EditForm.AddNew.NewContextInfoDisabled')
        : t('forms:Dialogs.EditForm.AddNew.NewContextInfo'),
    },
  ];

  const toMenuItem = (option: IFormStructureAddOption): IContextualMenuItem => ({
    key: option.key,
    text: option.label,
    iconProps: { iconName: option.icon },
    onClick: option.subMenuItems ? undefined : option.onClick,
    subMenuProps: option.subMenuItems ? { items: option.subMenuItems } : undefined,
    disabled: option.disabled,
    title: option.title,
  });

  const criteriaFormEmpty = getVisibleFormElements(props.formElements, props.showRetired).length === 0;
  const formAddOptions = props.criteriaOnly ? criteriaAddOptions(false) : [...criteriaAddOptions(false), ...addOptions];
  const criteriaCommandBarItems: ICommandBarItemProps[] = [];

  if (!criteriaFormEmpty) {
    criteriaCommandBarItems.push({
      key: 'add',
      text: t('translation:General.Button.Add'),
      iconProps: { iconName: 'Add' },
      subMenuProps: { items: formAddOptions.map(toMenuItem) },
    });
  }

  if (criteriaActionBindings.length > 0) {
    criteriaCommandBarItems.push({
      key: 'criteriaActions',
      commandBarButtonAs: () => (
        <AIActionsButton variant={AIActionsButtonVariant.CommandBar} bindings={criteriaActionBindings} />
      ),
    });
  }

  criteriaCommandBarItems.push(...(props.criteriaCommandBarItems ?? []));

  if (!criteriaFormEmpty) {
    criteriaCommandBarItems.push(
      ...(props.toolbarCommands ?? []).map((command) => ({
        key: command.key,
        text: command.label,
        iconProps: { iconName: command.icon },
        disabled: command.disabled,
        title: command.title,
        onClick: command.onClick,
      })),
    );
  }

  const renderCriteriaCommandBar = () => (
    <IPCommandBar items={criteriaCommandBarItems} styles={{ root: { padding: 0 } }} />
  );

  const showCriteriaCommandBar = canManageCriteria && criteriaEntities.length > 0;

  //counted the way the preview below counts it: a retired criterion nobody answered is not rendered,
  //so a form holding only those looks empty and gets the onboarding cards rather than the menu
  const renderAddControl = () => {
    return (
      <FormStructureAddControl
        options={
          props.criteriaOnly
            ? criteriaAddOptions(criteriaFormEmpty)
            : [...criteriaAddOptions(criteriaFormEmpty), ...addOptions]
        }
        commands={props.toolbarCommands}
        variant={criteriaFormEmpty ? 'cards' : 'menu'}
      />
    );
  };

  const fieldPanelAsPanel = props.fieldPanelAsPanel ?? true;
  const formFieldModalAsPanel = props.formFieldModalAsPanel ?? fieldPanelAsPanel;
  const showLivePreview = fieldPanelAsPanel && !appContext.isMobileView;
  const addPreviewToggleId = 'fieldPreviewToggleAdd';
  const editPreviewToggleId = 'fieldPreviewToggleEdit';
  const previewTargetId = showAddPanel
    ? addPreviewToggleId
    : showEditPanel
    ? editPreviewToggleId
    : 'fieldsPreviewAnchor';
  const sidePanelPreviewGap = -14;

  // The form-preview wrapper must be height:100% (not the default auto): TaskTypeFormRenderer uses
  // verticalFill + a ScrollableStackItem that need a real height from their parent, or the preview
  // collapses to its min-height and clips the form. It also doubles as #fieldsPreviewAnchor — the
  // anchor for the operand drill-in preview (KPIPreviewModal) when no Add/Edit field panel is open.
  // With disableScrollContainer the form flows instead, so it must NOT be given a height: 100% of an
  // auto-height parent is auto, which would collapse it to the renderer's min-height.
  return (
    <div
      id="fieldsPreviewAnchor"
      style={{ height: props.disableScrollContainer ? undefined : '100%', position: 'relative' }}
    >
      {(isCriteriaWorking || (props.criteriaOnly && props.isLoading)) && <OverlayLoader text={criteriaWorkingText} />}
      <TaskTypeFormRenderer
        task={task}
        links={taskLinks}
        formElements={props.formElements}
        isLoading={props.isLoading}
        allowEdit={props.editable}
        readonly={props.readonly}
        compact={props.compact ?? true}
        hideEmptyFormText={props.hideEmptyFormText}
        disableScrollContainer={props.disableScrollContainer}
        onUpdateValue={onUpdateValue}
        onUpdateKPI={updateKPILocal}
        onSetKPI={updateKPILocal}
        onUpdateTaskForForm={setTask}
        addLinks={(links) => setTaskLinks((cur) => [...cur, ...links])}
        setLinks={setTaskLinks}
        removeLink={(link) => setTaskLinks((cur) => cur.filter((l) => l.linkId !== link.linkId))}
        onUpdateField={onUpdateField}
        onRemove={props.criteriaOnly ? undefined : onRemove}
        onEdit={props.criteriaOnly ? undefined : onEdit}
        onAddAttachment={props.criteriaOnly ? undefined : onAddAttachment}
        onAddContext={props.criteriaOnly ? undefined : onAddContext}
        onMoveUp={props.criteriaOnly ? undefined : onMoveUp}
        onMoveDown={props.criteriaOnly ? undefined : onMoveDown}
        isSystemForm={props.isSystemForm}
        onAddNewKPI={props.criteriaOnly ? undefined : onAddNewField}
        fieldClassName={props.showFieldCards ? styles.fieldCard : undefined}
        denseFields={props.showFieldCards}
        isFieldSelected={(field) =>
          (showEditPanel && !!kpiToEdit && field.kpi === kpiToEdit) ||
          (showFieldEditor && !!formElementToEdit && field === formElementToEdit)
        }
        renderAddButton={props.editable ? renderAddControl : undefined}
        renderCommandBar={showCriteriaCommandBar ? renderCriteriaCommandBar : undefined}
        kpis={props.kpis}
        designTime={props.designTime}
        showRetired={props.showRetired}
        entityNamesByKey={props.designTime ? entityNamesWithLayer : props.entityNamesByKey}
        evidenceTasksByCriterionKey={props.evidenceTasksByCriterionKey}
        criterionBadgesByKey={props.criterionBadgesByKey}
        onEditCriterion={canManageCriteria ? onEditCriterion : undefined}
        onRemoveCriterion={canManageCriteria ? (field) => setCriterionToRemove(field) : undefined}
        onMoveCriterionUp={canManageCriteria ? (field) => void moveCriterion(field, -1) : undefined}
        onMoveCriterionDown={canManageCriteria ? (field) => void moveCriterion(field, 1) : undefined}
        onDragMove={props.editable ? onDragMove : undefined}
      />
      {showAddPanel && (
        <FieldPanel
          key={addPanelKey}
          mode="add"
          isOpen={showAddPanel}
          renderAsPanel={fieldPanelAsPanel}
          onDismiss={() => {
            setShowAddPanel(false);
            setKPIToPreview(undefined);
          }}
          isLoading={isSaving}
          kpis={props.kpis}
          formKpiIds={formKpiIds}
          updatePreviewKPI={showLivePreview ? updatePreviewKPI : undefined}
          onAddNewKPI={addKPI}
          defaultIsField={addPanelSeed.isField}
          defaultName={addPanelSeed.name}
          hideReuse={addPanelSeed.fromCreateNew}
          candidates={reuseCandidates}
          onReuse={(kpi) => {
            attachKPIs([kpi]);
            setShowAddPanel(false);
            setKPIToPreview(undefined);
          }}
          onCreateNew={(name) => {
            // Open a fresh "new KPI" panel: re-seed (typed name + tracked KPI) and bump the key so the
            // panel remounts and animates in, instead of silently staying on the current form.
            setKPIToPreview(undefined);
            setAddPanelSeed({ name: name || undefined, isField: false, fromCreateNew: true });
            setAddPanelKey((k) => k + 1);
          }}
          previewDismissed={showLivePreview ? previewDismissed : undefined}
          onTogglePreview={showLivePreview ? togglePreview : undefined}
          previewToggleId={addPreviewToggleId}
        />
      )}
      {showEditPanel && kpiToEdit && (
        <FieldPanel
          mode="edit"
          isOpen={showEditPanel}
          renderAsPanel={fieldPanelAsPanel}
          onDismiss={() => {
            setShowEditPanel(false);
            setKPIToPreview(undefined);
          }}
          isLoading={isSaving}
          kpis={props.kpis}
          formKpiIds={formKpiIds}
          updatePreviewKPI={showLivePreview ? updatePreviewKPI : undefined}
          kpiToEdit={kpiToEdit}
          onEditKPI={editKPI}
          taskTypeContext={props.taskTypeId}
          previewDismissed={showLivePreview ? previewDismissed : undefined}
          onTogglePreview={showLivePreview ? togglePreview : undefined}
          previewToggleId={editPreviewToggleId}
        />
      )}
      {elementToRemove && (
        <DialogYesNo
          hidden={false}
          title={t('forms:Dialogs.EditForm.RemoveConfirmTitle')}
          subText={t('forms:Dialogs.EditForm.RemoveConfirmText')}
          onNo={() => setElementToRemove(undefined)}
          onYes={() => {
            removeElement(elementToRemove);
            setElementToRemove(undefined);
          }}
        />
      )}
      {showFieldEditor && formElementToEdit && (
        <FormFieldModal
          key={formElementToEdit.key}
          field={formElementToEdit}
          addMode={fieldFormAddMode}
          formElements={props.formElements}
          isOpen={showFieldEditor}
          onDismiss={() => setShowFieldEditor(false)}
          onUpdate={onUpdateField}
          onAdd={addField}
          isLoading={isSaving || props.isLoading}
          renderAsPanel={formFieldModalAsPanel}
        />
      )}
      {criterionTarget && (
        <AuditCriterionPanel
          isOpen={true}
          entityType={criterionTarget.entityType}
          entityId={criterionTarget.entityId}
          set={criterionSet}
          criterionKey={criterionTarget.criterionKey}
          onSaved={(set) => {
            setCriterionSet(set);
            props.onCriteriaChanged?.();
          }}
          onDismiss={() => setCriterionTarget(undefined)}
        />
      )}
      {generateTarget && (
        <DialogConfirmGeneric
          hidden={false}
          title={t('criteria:Editor.RegenerateConfirmTitle')}
          subText={t('criteria:Editor.RegenerateConfirm')}
          onYes={() => void generateCriteria()}
          onNo={() => setGenerateTarget(undefined)}
        />
      )}
      {criterionToRemove && (
        <DialogYesNo
          hidden={false}
          title={t('criteria:Editor.DeleteConfirmTitle')}
          subText={t('criteria:Editor.DeleteConfirm')}
          onYes={() => void deleteCriterion(criterionToRemove)}
          onNo={() => setCriterionToRemove(undefined)}
        />
      )}
      {pendingKPIDefinition && (
        <GenericModal
          title={t('forms:Versioning.SharedKPIDefinitionTitle')}
          subTitle={t('forms:Versioning.SharedKPIDefinitionText', {
            forms: pendingKPIDefinition.preview.affectedCatalogFamilyCount,
            tasks: pendingKPIDefinition.preview.affectedTaskCount,
          })}
          isOpen={true}
          isActionPending={isSaving}
          keepOpenAfterSave={true}
          saveDisabled={!kpiDefinitionReason.trim() || pendingKPIDefinition.preview.inaccessibleCatalogFamilyCount > 0}
          saveButtonText={t('forms:Versioning.Apply')}
          onClose={() => setPendingKPIDefinition(undefined)}
          onSave={async () => {
            const accepted = await commitKPIDefinition(
              pendingKPIDefinition.kpi,
              pendingKPIDefinition.preview,
              kpiDefinitionReason.trim(),
            );
            if (accepted) {
              setShowEditPanel(false);
            }
          }}
        >
          {pendingKPIDefinition.preview.affectedCatalogFamilyNames.length > 0 && (
            <IPText>
              {t('forms:Versioning.SharedKPIDefinitionForms', {
                forms: pendingKPIDefinition.preview.affectedCatalogFamilyNames.join(', '),
              })}
            </IPText>
          )}
          {pendingKPIDefinition.preview.inaccessibleCatalogFamilyCount > 0 && (
            <IPText>
              {t('forms:Versioning.SharedKPIDefinitionInaccessible', {
                count: pendingKPIDefinition.preview.inaccessibleCatalogFamilyCount,
              })}
            </IPText>
          )}
          <IPText>{t('forms:Versioning.SharedKPIDefinitionReasonDescription')}</IPText>
          <IPTextField
            multiline
            autoAdjustHeight
            label={t('forms:Versioning.SharedKPIDefinitionReason')}
            value={kpiDefinitionReason}
            onChange={(_, value) => setKPIDefinitionReason(value ?? '')}
          />
        </GenericModal>
      )}
      {showLivePreview && kpiToPreview && !previewDismissed && (
        <KPIPreviewModal
          isOpen={kpiToPreview !== undefined}
          onClose={props.persistPreviewDismissed ? togglePreview : () => setKPIToPreview(undefined)}
          kpi={kpiToPreview}
          kpis={props.kpis}
          targetId={previewTargetId}
          gapSpace={showAddPanel || showEditPanel ? sidePanelPreviewGap : undefined}
          onUpdateKPI={(kpi) => setKPIToPreview(kpi)}
          onUpdateValue={(kpi, data) => {
            // A new object, not the same one changed in place: React skips the render for an unchanged
            // reference, and a controlled input (text, number, date) then snaps back to its old value.
            const next = kpi.clone();
            next.data = [data];
            setKPIToPreview(next);
          }}
          allowSwitchView={true}
        />
      )}
    </div>
  );
};

export default TaskTypeFormEditor;
